import * as React from 'react'

import moment from 'moment'
import { ActivityModel } from '@/modules/drivers/types/activities'
import { ActivityType } from '@/app/constants/activity'

import { getActivityHeight } from '@/modules/drivers/helpers/ActivityHeightResolver'
import { TIME_FORMAT } from '@/app/constants/date'
import { getActivityColor } from '@/modules/drivers/helpers/ActivityColorResolver'
import { getActivityIcon } from '@/modules/drivers/helpers/ActivityIconResolver'
import { getDurationBetweeenDates } from '@/app/common/dateHelper'

import { FaIcon } from '@/lib/fa'
import { getActivityType } from '@/modules/drivers/helpers/ActivityTypeResolver'
import { useActivityContextMenu } from '@/modules/drivers/tabs/activities/context/ActivityContextMenuContext'
import { TripEventType } from '@/app/constants/delegation/tripEventType'
import { getEventTypeIcon } from '@/app/common/converters/newDelegation/tripEventTypeToIconConverter'

import { useScene } from '../context/SceneContext'
import { useConfig } from '../context/ConfigContext'
import { useTimelineData } from '../context/DataContext'
import { REST_BLOCK_HEIGHT } from '../utils/layout'
import { PRINT_SEGMENT_CLASS, segmentMark } from '../printFacts'
import type { SegmentMark } from '../printFacts'
import { PRINT_BAR_OUTLINE } from '../printStyles'
import ferryIcon from '@/assets/icons_nucleo/icons/mix/ferry.png'

const SHOW_TIME_MIN_WIDTH = 55
// Kolor WPF dla aktywności na nieaktywnym pojeździe (RGB 210, 210, 210).
const INACTIVE_VEHICLE_COLOR = '#d2d2d2'
const SHOW_ICON_MIN_WIDTH = 20

const Hatch: React.FC<{
  width: number
  height: number
  className: string
  onMouseEnter: () => void
  onMouseLeave: () => void
}> = ({ width, height, className, onMouseEnter, onMouseLeave }) => (
  <div
    style={{
      position: 'absolute',
      top: 0,
      left: 0,
      width,
      height,
      zIndex: 12,
    }}
    className={className}
    onMouseEnter={onMouseEnter}
    onMouseLeave={onMouseLeave}
  />
)

const PrintSegmentMarks: React.FC<{
  mark: SegmentMark
  width: number
  height: number
}> = ({ mark, width, height }) => {
  switch (mark.kind) {
    case 'neither':
      return null
    case 'manual':
      return (
        <div
          className={PRINT_SEGMENT_CLASS.manual}
          style={{ position: 'absolute', top: 0, left: 0, zIndex: 13 }}
        />
      )
    case 'edited':
      return (
        <div
          className={PRINT_SEGMENT_CLASS.edited}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width,
            height,
            zIndex: 12,
          }}
        />
      )
    case 'both':
      return (
        <>
          <div
            className={PRINT_SEGMENT_CLASS.edited}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width,
              height,
              zIndex: 12,
            }}
          />
          <div
            className={PRINT_SEGMENT_CLASS.manual}
            style={{ position: 'absolute', top: 0, left: 0, zIndex: 13 }}
          />
        </>
      )
    default: {
      const _exhaustive: never = mark
      return _exhaustive
    }
  }
}

export type ActivityBlockProps = {
  activity: ActivityModel
  index: number
  isSelected: boolean
  isEditing: boolean
  isAddingNew: boolean
  currentDate?: any
  parentLeftPosition: number
}

const RestBaseIcon: React.FC<{
  side: 'left' | 'right'
  type: TripEventType
}> = ({ side, type }) => (
  <div
    style={{
      position: 'absolute',
      left: side === 'left' ? -10 : undefined,
      right: side === 'right' ? -9 : undefined,
      top: -34,
      bottom: 0,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'flex-start',
    }}
  >
    <div className="tw-grid tw-h-5 tw-w-5 tw-place-items-center tw-rounded-full tw-border tw-border-primary tw-bg-primary tw-shadow-sm">
      <FaIcon
        icon={getEventTypeIcon(type)}
        className="tw-h-2.5 tw-w-2.5 tw-text-white"
      />
    </div>
    <div className="tw-w-[1px] tw-flex-1 tw-bg-primary" />
  </div>
)

export const ActivityBlock: React.FC<ActivityBlockProps> = React.memo(
  (props: ActivityBlockProps) => {
    const scene = useScene()
    const config = useConfig()
    const { allEvents } = useTimelineData()
    const [isEditing, setIsEditing] = React.useState<boolean>(false)
    const [isHoveredRestBlock, setIsHoveredRestBlock] =
      React.useState<boolean>(false)
    const { openMenu } = useActivityContextMenu()

    const activity = props.activity
    const activityType = getActivityType(activity.type)
    const heightFactor = getActivityHeight(activityType)


    const restClickEnabled =
      !!config.isRestsOnBaseModeOn &&
      activityType === ActivityType.REST &&
      !!config.onRestClick

    const restBaseEventInfo = React.useMemo(() => {
      const empty = {
        hasBegin: false,
        hasFinish: false,
        nextType: null as TripEventType | null,
        isRealStartInView: false,
        isRealEndInView: false,
      }
      if (
        !config.isRestsOnBaseModeOn ||
        !allEvents ||
        activityType !== ActivityType.REST
      ) {
        return empty
      }
      const restStart = moment(activity.startTime)
      const restEnd = moment(activity.endTime)

      const viewStart = scene.startTime
      const viewEnd = scene.startTime.clone().add(scene.days, 'days')
      const isRealStartInView =
        restStart.isSameOrAfter(viewStart) && restStart.isBefore(viewEnd)
      const isRealEndInView =
        restEnd.isAfter(viewStart) && restEnd.isSameOrBefore(viewEnd)

      const matching = allEvents.filter(
        (e) =>
          (e.tripEvent === TripEventType.Begin ||
            e.tripEvent === TripEventType.Finish) &&
          moment(e.date).isSameOrAfter(restStart) &&
          moment(e.date).isSameOrBefore(restEnd),
      )
      const hasBegin = matching.some((e) => e.tripEvent === TripEventType.Begin)
      const hasFinish = matching.some(
        (e) => e.tripEvent === TripEventType.Finish,
      )

      let nextType: TripEventType | null = null
      if (hasBegin && hasFinish) {
        nextType = null
      } else if (hasBegin) {
        nextType = TripEventType.Finish
      } else if (hasFinish) {
        nextType = TripEventType.Begin
      } else {
        const sortedRelevant = [...allEvents]
          .filter(
            (e) =>
              e.tripEvent === TripEventType.Begin ||
              e.tripEvent === TripEventType.Finish,
          )
          .sort((a, b) =>
            moment(a.date).isBefore(moment(b.date))
              ? -1
              : moment(a.date).isAfter(moment(b.date))
                ? 1
                : 0,
          )
        const lastEvent = sortedRelevant[sortedRelevant.length - 1]
        nextType =
          lastEvent?.tripEvent === TripEventType.Begin
            ? TripEventType.Finish
            : TripEventType.Begin
      }

      return { hasBegin, hasFinish, nextType, isRealStartInView, isRealEndInView }
    }, [
      allEvents,
      activity.startTime,
      activity.endTime,
      activityType,
      config.isRestsOnBaseModeOn,
      scene.startTime,
      scene.days,
    ])

    const hasRestBaseEvent =
      restBaseEventInfo.hasBegin || restBaseEventInfo.hasFinish

    const renderRestBaseOverlay = () => (
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background:
            restBaseEventInfo.hasBegin && restBaseEventInfo.hasFinish
              ? 'rgba(0,0,0,0.45)'
              : hasRestBaseEvent
                ? 'rgba(0,0,0,0.25)'
                : 'rgba(0,0,0,0.18)',
          pointerEvents: 'none',
          zIndex: 12,
        }}
      >
        {restBaseEventInfo.hasFinish && restBaseEventInfo.isRealStartInView && (
          <RestBaseIcon side="left" type={TripEventType.Finish} />
        )}
        {restBaseEventInfo.hasBegin && restBaseEventInfo.isRealEndInView && (
          <RestBaseIcon side="right" type={TripEventType.Begin} />
        )}
        {isHoveredRestBlock &&
          restBaseEventInfo.nextType === TripEventType.Finish &&
          !restBaseEventInfo.hasFinish &&
          restBaseEventInfo.isRealStartInView && (
            <RestBaseIcon side="left" type={TripEventType.Finish} />
          )}
        {isHoveredRestBlock &&
          restBaseEventInfo.nextType === TripEventType.Begin &&
          !restBaseEventInfo.hasBegin &&
          restBaseEventInfo.isRealEndInView && (
            <RestBaseIcon side="right" type={TripEventType.Begin} />
          )}
      </div>
    )

    const width = scene.widthOf(
      activity.startTime,
      activity.endTime,
      props.currentDate,
    )
    const flattenScale = config.flattenChart ? 0.7 : 1
    const height = REST_BLOCK_HEIGHT * heightFactor * flattenScale
    const left = scene.timeToX(activity.startTime)

    const showTime = !scene.weekly && width > SHOW_TIME_MIN_WIDTH
    const showIcon = !scene.weekly && width > SHOW_ICON_MIN_WIDTH

    const iconClass = width > 45 ? 'tw-h-3 tw-w-3' : 'tw-h-2 tw-w-2'
    const iconSize = width > 45 ? 12 : 8
    const showFerryInline = activity.isFerryCrossing && showTime

    const handleContextMenu = React.useCallback(
      (e: React.MouseEvent) => {
        if (config.onActivityContextMenu) {
          e.preventDefault()
          e.stopPropagation()
          config.onActivityContextMenu(props.activity, e.clientX, e.clientY)
          return
        }
        if (!config.canEdit && !config.canGenerateAttestation) return
        e.preventDefault()
        e.stopPropagation()

        const positionX = e.clientX - props.parentLeftPosition - scene.gutter
        const addStartTime = scene.xToTime(positionX).startOf('minute')

   
        const addOnly = !!activity?.isVehicleSource

        config.onContextMenuChanged(true, props.activity)
        if (!addOnly) setIsEditing(true)

        openMenu({
          x: e.clientX,
          y: e.clientY,
          activity: props.activity,
          canEdit: config.canEdit,
          canGenerateAttestation: config.canGenerateAttestation || false,
          translate: config.translate,
          onAdd: config.onAdd,
          onEdit: config.onEdit,
          onContextMenuChanged: (value, act) => {
            config.onContextMenuChanged(value, act)
            if (!value) setIsEditing(false)
          },
          addStartTime,
          addOnly,
        })
      },
      [scene, config, openMenu, props.activity, props.parentLeftPosition],
    )

    return (
      <div
        className="activity-block tw-flex tw-flex-col tw-justify-end"
        onContextMenu={handleContextMenu}
        style={{ position: 'absolute', left, top: 0, bottom: 0, paddingBottom: scene.bottomPadding }}
      >
        <div style={{ position: 'relative' }}>
          <div style={{ position: 'absolute', bottom: 0 }}>
            <div
              className="tw-rounded-t-[4px] tw-flex tw-justify-center tw-items-start"
              onMouseEnter={() => {
                config.onSelected && config.onSelected(props.activity)
                if (restClickEnabled) setIsHoveredRestBlock(true)
              }}
              onMouseLeave={() => {
                config.onSelected && config.onSelected(null)
                if (restClickEnabled) setIsHoveredRestBlock(false)
              }}
              onClick={
                restClickEnabled
                  ? () =>
                      config.onRestClick && config.onRestClick(props.activity)
                  : undefined
              }
              data-tip={JSON.stringify(activity)}
              data-for="activityTooltip"
              style={{
                position: 'relative',
                background:
                  (isEditing || props.isEditing) && !props.isAddingNew
                    ? '#909090'
                    : config.markInactiveVehicles &&
                        activity.isVehicleActive === false
                      ? INACTIVE_VEHICLE_COLOR
                      : getActivityColor(activity),
                width,
                height,
                boxShadow: config.printMode ? PRINT_BAR_OUTLINE : undefined,
                paddingTop: 1,
                pointerEvents: 'auto',
                cursor: restClickEnabled ? 'crosshair' : undefined,
                zIndex: activityType == ActivityType.REST ? 9 : 11,
              }}
              key={props.index}
            >
              {config.isRestsOnBaseModeOn &&
                activityType === ActivityType.REST &&
                (isHoveredRestBlock || hasRestBaseEvent) &&
                renderRestBaseOverlay()}
              <div
                className="tw-flex tw-items-center tw-gap-1 tw-pt-1"
                style={{ color: '#000' }}
              >
                {showIcon && (
                  <FaIcon
                    className={iconClass}
                    icon={getActivityIcon(activity.type)}
                    width={iconSize}
                    height={iconSize}
                  />
                )}
                {showTime && (
                  <span className="tw-text-xs" style={{ color: '#000' }}>
                    {getDurationBetweeenDates(
                      moment(activity.startTime).toDate(),
                      moment(activity.endTime).toDate(),
                    )}
                  </span>
                )}
              </div>

              {showFerryInline && (
                <img
                  style={{ marginTop: 0, marginLeft: 5 }}
                  src={ferryIcon}
                  width={iconSize}
                  height={iconSize}
                />
              )}
            </div>
            {props.isSelected && (
              <div
                className="tw-flex tw-justify-center tw-items-start"
                style={{
                  opacity: props.isSelected ? 0.2 : 0,
                  background: '#000000',
                  width,
                  height,
                  paddingTop: 1,
                  zIndex: 100,
                  position: 'absolute',
                  top: 0,
             
                  pointerEvents: 'none',
                }}
              ></div>
            )}

            {config.printMode ? (
              <PrintSegmentMarks
                mark={segmentMark(activity)}
                width={width}
                height={height}
              />
            ) : (
              <>
                {activity.isModifiedByUser && (
                  <Hatch
                    width={width}
                    height={height}
                    className="activity"
                    onMouseEnter={() => {
                      config.onSelected && config.onSelected(props.activity)
                    }}
                    onMouseLeave={() => {
                      config.onSelected && config.onSelected(null)
                    }}
                  />
                )}
                {activity.isManualEntry && (
                  <Hatch
                    width={width}
                    height={height}
                    className="activity"
                    onMouseEnter={() => {
                      config.onSelected && config.onSelected(props.activity)
                    }}
                    onMouseLeave={() => {
                      config.onSelected && config.onSelected(null)
                    }}
                  />
                )}
              </>
            )}

            {activity.isCrew && (
              <div
                style={{
                  background: '#000000',
                  position: 'absolute',
                  bottom: -3,
                  height: 1,
                  width: width,
                }}
              />
            )}
          </div>
        </div>
      </div>
    )
  },
  (prevProps: ActivityBlockProps, nextProps: ActivityBlockProps) => {
    if (nextProps.activity.id !== prevProps.activity.id) return false
    if (nextProps.activity.type !== prevProps.activity.type) return false
    if (nextProps.isSelected !== prevProps.isSelected) return false
    if (nextProps.isEditing !== prevProps.isEditing) return false
    if (nextProps.isAddingNew !== prevProps.isAddingNew) return false
    if (nextProps.parentLeftPosition !== prevProps.parentLeftPosition)
      return false
    if (nextProps.activity.isCrew !== prevProps.activity.isCrew) return false
    if (
      nextProps.activity.isFerryCrossing !== prevProps.activity.isFerryCrossing
    )
      return false
    if (nextProps.activity.isManualEntry !== prevProps.activity.isManualEntry)
      return false
    if (nextProps.activity.isOutOfScope !== prevProps.activity.isOutOfScope)
      return false
    if (
      nextProps.activity.isVehicleActive !== prevProps.activity.isVehicleActive
    )
      return false
    if (
      nextProps.activity.isModifiedByUser !==
      prevProps.activity.isModifiedByUser
    )
      return false

    if (
      moment(nextProps.activity.startTime).format(TIME_FORMAT) !==
      moment(prevProps.activity.startTime).format(TIME_FORMAT)
    )
      return false

    if (
      moment(nextProps.activity.endTime).format(TIME_FORMAT) !==
      moment(prevProps.activity.endTime).format(TIME_FORMAT)
    )
      return false

    return true
  },
)

ActivityBlock.displayName = 'ActivityBlock'
